import { useTranslation } from 'react-i18next';
import { formatDayLongTime, setWebTitle } from '@/lib/format-utils';
import { useSystemLogs, useUsers } from '@/hooks/api-hooks';
import { SkeletonList } from '@/components/snippets';
import { useMemo, useState } from 'react';
import { User } from '@turizi/shared';
import SearchInput from '@/components/search-input';
import { filterByMultiWordSearch } from '@/utils/search-utils';
import { ApiLogAction, ImportantDataDisplay } from '@/components/api-log-action';


export default function HistoryPage(): JSX.Element {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');
    const {
        data: logsResponse,
        isLoading
    } = useSystemLogs();
    const {
        data: usersResponse,
        isLoading: isLoadingUsers
    } = useUsers();

    const users = usersResponse?.data || [];

    setWebTitle(t('history'));

    const usersMap = useMemo(() => {
        return users.reduce((acc, user) => {
            acc[user.id] = user;
            return acc;
        }, {} as Record<number, User>);
    }, [users]);

    const logs = logsResponse?.data || [];

    const filteredLogs = useMemo(() => {
        return filterByMultiWordSearch(logs, searchTerm, (log) => {
            const user = usersMap[log.user_id];
            return [
                formatDayLongTime(log.created_at),
                user?.email || '',
                log.method,
                log.url,
                JSON.stringify(log.request_body),
                log.ip_address || ''
            ].join(' ');
        });
    }, [logs, searchTerm, usersMap]);

    return (
        <div className='space-y-4'>
            <SearchInput
                onSearch={setSearchTerm}
            />
            <div className="panel-content">
                <div className="subtle-table overflow-x-hidden">
                    <table>
                        <tbody>
                            {filteredLogs.map((log) => (
                                <tr key={log.id}>
                                    <td>
                                        <div className="text-xs text-gray-400 font-bold">
                                            {formatDayLongTime(log.created_at)}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="text-xs text-gray-600">
                                            {usersMap[log.user_id]?.email || '-'}
                                        </div>
                                    </td>
                                    <td>
                                        <div className='px-14'>
                                            <ApiLogAction method={log.method} url={log.url} body={log.request_body} />
                                        </div>
                                    </td>
                                    <td>
                                        <ImportantDataDisplay body={log.request_body} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {isLoading && <SkeletonList />}
            </div>
        </div>
    );
}